'use client';

import { use, useMemo } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { FormManifestContext } from './FormBuilder';
export type StepValidationState = {
  stepKey: string;
  stepName: string;
  isSubmitted: boolean;
  isValid: boolean;
  isInvalid: boolean;
  errorCount: number;
  isSkipped: boolean;
  isComplete: boolean;
  isPristine: boolean;
};
export type StepStateMap = Record<string, StepValidationState>;
export type UseStepValidationResult = {
  stepStateMap: StepStateMap;
  activeStepIsValid: boolean;
};
type UseStepValidationArgs = {
  activeStep: string;
  visitedStepKeys: Set<string>;
  submittedStepKeys: Set<string>;
};
export function useStepValidation({
  activeStep,
  visitedStepKeys,
  submittedStepKeys
}: UseStepValidationArgs): UseStepValidationResult {
  const manifest = use(FormManifestContext);
  const {
    getValues
  } = useFormContext();
  const watchedFieldNames = useMemo(() => manifest.flattenedFields.map(f => f.id), [manifest.flattenedFields]);
  const {
    errors,
    touchedFields
  } = useFormState({
    name: watchedFieldNames
  });
  const activeStepFieldKeys = useMemo(() => manifest.fieldKeysBySection.get(activeStep) ?? [], [manifest.fieldKeysBySection, activeStep]);
  const watchedActiveStepValues = useWatch({
    name: activeStepFieldKeys
  }) as unknown[];
  const activeStepValues = useMemo(() => {
    return Object.fromEntries(activeStepFieldKeys.map((key, index) => [key, watchedActiveStepValues[index]]));
  }, [activeStepFieldKeys, watchedActiveStepValues]);
  const activeStepIsValid = useMemo(() => {
    const schema = manifest.stepSchema.get(activeStep);
    if (!schema) return true;
    return schema.safeParse(activeStepValues).success;
  }, [manifest.stepSchema, activeStep, activeStepValues]);
  const values = getValues();
  const activeStepIndex = manifest.steps.findIndex(s => s._key === activeStep);
  const stepStateMap = Object.fromEntries(manifest.steps.map((step, stepIndex) => {
    const fieldKeys = manifest.fieldKeysBySection.get(step._key) ?? [];
    const rhfErrorCount = fieldKeys.filter(key => errors[key]).length;
    const hasTouched = fieldKeys.some(key => touchedFields[key]);
    const hasValues = fieldKeys.some(key => {
      const v = values[key];
      return v !== undefined && v !== '' && v !== null;
    });
    const stepValues = Object.fromEntries(fieldKeys.map(key => [key, values[key]]));
    const isVisited = visitedStepKeys.has(step._key);
    const isSubmitted = submittedStepKeys.has(step._key);
    const isPastStep = activeStepIndex >= 0 && stepIndex >= 0 && stepIndex < activeStepIndex;
    const schema = manifest.stepSchema.get(step._key);
    const schemaResult = schema?.safeParse(stepValues);
    const schemaErrorCount = schemaResult && !schemaResult.success ? new Set(schemaResult.error.issues.map(issue => issue.path[0])).size : 0;
    const errorCount = rhfErrorCount || schemaErrorCount;
    const isValid = schemaResult?.success ?? true;
    const isInvalid = rhfErrorCount > 0 || isPastStep && !isValid;
    const isSkipped = isPastStep && !hasValues && isValid;
    const isComplete = !!((isSubmitted || isPastStep) && hasValues && isValid);
    const isPristine = !hasTouched && !isVisited;
    return [step._key, {
      stepKey: step._key,
      stepName: step.name ?? step._key,
      isSubmitted,
      isValid,
      isInvalid,
      errorCount,
      isSkipped,
      isComplete,
      isPristine
    } satisfies StepValidationState];
  })) as StepStateMap;
  return {
    stepStateMap,
    activeStepIsValid
  };
}